import React, { useId, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon, SparklesIcon } from 'lucide-react';
type MaterialSample = {
  title: string;
  category: string;
  description: string;
  imageUrl: string;
};
const samples: MaterialSample[] = [{
  title: 'Tavola progressioni',
  category: 'Schiena, gambe e fianchi',
  description: 'Indicazioni chiare per adattare ogni esercizio ai livelli L1, L2 e L3.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/6oYnLa1ThkS8VQ9MuejTq5/Captura-de-pantalla-2026-07-26-a-las-7.09.11_p._m..webp'
}, {
  title: 'Lezione con Foam Roller',
  category: 'Programmazione mensile',
  description: 'Una lezione completa con obiettivi, tempi, contenuti e cue verbali pronti.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/arTMbxxVjJCjuLaCd9CRWS/Captura-de-pantalla-2026-07-26-a-las-7.11.13_p._m..webp'
}, {
  title: 'Long Stretch Series',
  category: 'Sequenza Reformer',
  description: 'Progressioni visive e ripetizioni per guidare ogni blocco in totale sicurezza.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/35cUw7MxRN6wbe8nM6xcSX/Captura-de-pantalla-2026-07-26-a-las-7.13.02_p._m..webp'
}, {
  title: 'Roller — livello 2',
  category: 'Sessione pronta da 40 minuti',
  description: 'Una scheda passo-passo con esercizi, intensità e durata già organizzati.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/oE6R362DeMLpe8ySKvkAcQ/Captura-de-pantalla-2026-07-26-a-las-7.13.47_p._m..webp'
}, {
  title: 'Sequenza per ernia lombare',
  category: 'Protocollo clinico',
  description: 'Una guida strutturata con note cliniche per insegnare con più sicurezza.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/nWt3a2mNtwKjrB5w89oqc2/Captura-de-pantalla-2026-07-26-a-las-7.14.47_p._m..webp'
}, {
  title: 'Secondo trimestre',
  category: 'Pilates in gravidanza · settimane 13–26',
  description: 'Indicazioni visive su movimenti consentiti, precauzioni e adattamenti per insegnare in sicurezza durante la gravidanza.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/toSnfRbyhAKQjaNhFRRUGe/Captura-de-pantalla-2026-07-26-a-las-7.20.06_p._m..webp'
}];
const pageVariants = {
  enter: (direction: number) => ({
    opacity: 0,
    x: direction > 0 ? 64 : -64,
    scale: 0.96
  }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1
  },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction > 0 ? -64 : 64,
    scale: 0.96
  })
};
export function MaterialPreviewSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const shouldReduceMotion = useReducedMotion();
  const carouselId = useId();
  const activeSample = samples[activeIndex];
  const previousSample = samples[(activeIndex - 1 + samples.length) % samples.length];
  const nextSample = samples[(activeIndex + 1) % samples.length];
  function showSlide(nextIndex: number) {
    const normalizedIndex = (nextIndex + samples.length) % samples.length;
    const goingForward = normalizedIndex > activeIndex && !(activeIndex === 0 && normalizedIndex === samples.length - 1) || activeIndex === samples.length - 1 && normalizedIndex === 0;
    setDirection(goingForward ? 1 : -1);
    setActiveIndex(normalizedIndex);
  }
  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      showSlide(activeIndex + 1);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showSlide(activeIndex - 1);
    }
  }
  return <section className="bg-[#fff7fb] px-5 py-20" aria-labelledby="material-preview-title" data-id="element-131" data-mp="3b89izmpxhhse">
      <div className="mx-auto max-w-6xl" data-id="element-132" data-mp="3g72nipn8lygf">
        <div className="mx-auto max-w-2xl text-center" data-id="element-133" data-mp="3g8475iibjt9r">
          <p className="inline-flex items-center gap-2 bg-emerald-500 px-4 py-2 text-xs font-bold uppercase tracking-[.2em] text-white" data-id="element-134" data-mp="327baos8gio40">
            <EyeIcon className="h-4 w-4" data-id="element-135" data-mp="375cwhkx9p1j6" />
            Anteprima gratuita
          </p>
          <h2 id="material-preview-title" className="mt-5 font-display text-5xl font-bold uppercase leading-[.92] text-[#241322] sm:text-7xl" data-id="element-136" data-mp="2z5yfpa2hffdv">
            Guarda il materiale{' '}
            <span className="text-emerald-700" data-id="element-137" data-mp="2pfne3u69tia5">da dentro</span>
          </h2>
          <p className="mt-5 text-sm font-medium leading-relaxed text-slate-600 sm:text-base" data-id="element-138" data-mp="2pdixqcr6qqsz">
            Sfoglia alcune pagine reali del pacchetto: schede visive, sequenze e
            protocolli già pronti per le tue lezioni.
          </p>
        </div>

        <div className="mx-auto mt-12 max-w-5xl outline-none" role="region" id={carouselId} aria-roledescription="carosello" aria-label="Anteprima del materiale Pilates Pronta" tabIndex={0} onKeyDown={handleKeyDown} data-id="element-139" data-mp="2bfckwkhhnrlt">
          <div className="overflow-hidden rounded-3xl border-4 border-[#241322] bg-[#241322] shadow-2xl" data-id="element-140" data-mp="22h8xtjquumrw">
            <div className="relative flex min-h-[460px] items-center justify-center overflow-hidden px-4 py-9 sm:min-h-[660px] sm:px-20 sm:py-12" data-id="element-141" data-mp="4ov612krvhyd">
              <img src={previousSample.imageUrl} alt="" aria-hidden="true" className="pointer-events-none absolute left-[3%] hidden max-h-[420px] w-auto -rotate-2 opacity-25 shadow-2xl lg:block" data-id="element-142" data-mp="1im7kx7dco97a" />
              <img src={nextSample.imageUrl} alt="" aria-hidden="true" className="pointer-events-none absolute right-[3%] hidden max-h-[420px] w-auto rotate-2 opacity-25 shadow-2xl lg:block" data-id="element-143" data-mp="1sazd9gmboviq" />

              <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-144" data-mp="znd8w659tmut">
                <motion.img key={activeSample.imageUrl} src={activeSample.imageUrl} alt={`Anteprima: ${activeSample.title}, ${activeSample.category}`} width={768} height={1365} custom={direction} variants={pageVariants} initial={shouldReduceMotion ? false : 'enter'} animate="center" exit={shouldReduceMotion ? undefined : 'exit'} transition={shouldReduceMotion ? {
                duration: 0
              } : {
                duration: 0.28,
                ease: [0.25, 1, 0.5, 1]
              }} className="relative z-10 max-h-[400px] w-auto max-w-full object-contain shadow-[0_28px_70px_rgba(0,0,0,.55)] ring-1 ring-white/20 sm:max-h-[580px]" data-id="element-145" data-mp="14lwwsc3fd8pt" />
              </AnimatePresence>

              <p className="absolute right-4 top-4 z-10 bg-emerald-500 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white sm:right-6 sm:top-6" data-id="element-146" data-mp="l000s1hz2m1w">
                Pagina {String(activeIndex + 1).padStart(2, '0')} /{' '}
                {String(samples.length).padStart(2, '0')}
              </p>

              <button type="button" onClick={() => showSlide(activeIndex - 1)} aria-controls={carouselId} aria-label="Mostra la pagina precedente" className="absolute left-2 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white text-[#241322] shadow-xl transition-transform duration-150 hover:scale-105 focus-visible:ring-4 focus-visible:ring-emerald-400 sm:left-5 sm:h-16 sm:w-16" data-id="element-147" data-mp="19xz84eflhqp">
                <ChevronLeftIcon className="h-7 w-7 sm:h-8 sm:w-8" data-id="element-148" data-mp="3epceask0szjy" />
              </button>
              <button type="button" onClick={() => showSlide(activeIndex + 1)} aria-controls={carouselId} aria-label="Mostra la pagina successiva" className="absolute right-2 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white text-[#241322] shadow-xl transition-transform duration-150 hover:scale-105 focus-visible:ring-4 focus-visible:ring-emerald-400 sm:right-5 sm:h-16 sm:w-16" data-id="element-149" data-mp="1u71wp4e6bhkd">
                <ChevronRightIcon className="h-7 w-7 sm:h-8 sm:w-8" data-id="element-150" data-mp="2cx1gbbr8w4ge" />
              </button>

              <p className="sr-only" aria-live="polite" data-id="element-151" data-mp="2hww5navdglzc">
                Pagina {activeIndex + 1} di {samples.length}:{' '}
                {activeSample.title}
              </p>
            </div>

            <div className="border-t border-white/10 bg-[#38233c] px-6 py-7 sm:px-10" data-id="element-152" data-mp="28v6fs9h51v3o">
              <div className="grid items-center gap-6 lg:grid-cols-[1.4fr_auto]" data-id="element-153" data-mp="shpr5wdftvlh">
                <div data-id="element-154" data-mp="sjspr4snox1t">
                  <p className="text-xs font-bold uppercase tracking-[.18em] text-emerald-300" data-id="element-155" data-mp="xh2c2bkr0slf">
                    {activeSample.category}
                  </p>
                  <h3 className="mt-2 font-display text-3xl font-bold uppercase leading-none text-white sm:text-4xl" data-id="element-156" data-mp="1o4i0k6jqv48w">
                    {activeSample.title}
                  </h3>
                  <p className="mt-3 max-w-xl text-sm font-medium leading-relaxed text-slate-300" data-id="element-157" data-mp="1f6g3u371mxrf">
                    {activeSample.description}
                  </p>
                </div>
                <a href="#offerta" className="cta-button flex w-full items-center justify-center rounded-2xl px-6 py-4 text-base font-bold lg:w-auto" data-id="element-158" data-mp="27tae67a5cgr6">
                  VOGLIO TUTTO IL MATERIALE
                </a>
              </div>

              <div className="mt-7 flex gap-3 overflow-x-auto border-t border-white/10 pt-6" aria-label={`Pagina ${activeIndex + 1} di ${samples.length}`} data-id="element-159" data-mp="1y2fo0rts4x0g">
                {samples.map((sample, index) => <button key={sample.title} type="button" onClick={() => showSlide(index)} aria-label={`Mostra ${sample.title}`} aria-current={index === activeIndex ? 'true' : undefined} className={`shrink-0 overflow-hidden border-2 transition-colors duration-150 ${index === activeIndex ? 'border-emerald-400' : 'border-transparent opacity-50 hover:opacity-100'}`} data-id="element-160" data-mp="2qn64l6sp6xdn" data-mpi={index}>
                    <img src={sample.imageUrl} alt="" loading="lazy" className="h-20 w-14 object-cover object-top sm:h-24 sm:w-16" data-id="element-161" data-mp="332w39xk53ghd" />
                  </button>)}
              </div>
            </div>
          </div>

          <p className="mt-5 flex flex-wrap items-center justify-center gap-2 text-center text-xs font-semibold text-slate-600" data-id="element-162" data-mp="3c3u7afukvnzy">
            <SparklesIcon className="h-4 w-4 text-emerald-700" data-id="element-163" data-mp="3h13wbue21xn9" />
            Dentro il pacchetto ci sono oltre 350 pagine come queste — usa le
            frecce o i tasti ← → per sfogliare.
          </p>
        </div>
      </div>
    </section>;
}